import { Severity } from 'support/types/ui';

import { WebEDIAnnotation, WebEDIAnnotationCode } from './types';

export type BannerSeverity = Extract<Severity, 'error' | 'warning'>;

export type CodedAnnotation = Extract<WebEDIAnnotation, { code: WebEDIAnnotationCode }>;

export type AnnotationPresentation = {
  severity: BannerSeverity;
  titleKey: string;
};

export const isCodedAnnotation = (annotation: WebEDIAnnotation): annotation is CodedAnnotation => {
  return 'code' in annotation;
};

// Single source of truth for how a coded annotation presents: its severity and the
// title translation key shared by the banner and the matching popover preset.
// CONNECTOR_HOOK splits on severity because it presents as either error or warning.
export const getCodedAnnotationPresentation = (annotation: CodedAnnotation): AnnotationPresentation => {
  switch (annotation.code) {
    case WebEDIAnnotationCode.UNMATCHED_ARTICLE:
      return { severity: 'warning', titleKey: 'webedi:fieldAnnotation.unmatchedArticleWarning.title' };
    case WebEDIAnnotationCode.EPHEMERAL_PARTNER:
      return { severity: 'warning', titleKey: 'webedi:fieldAnnotation.ephemeralPartnerWarning.title' };
    case WebEDIAnnotationCode.CONNECTOR_HOOK:
      return annotation.severity === 'error'
        ? { severity: 'error', titleKey: 'webedi:fieldAnnotation.connectorHookError.title' }
        : { severity: 'warning', titleKey: 'webedi:fieldAnnotation.connectorHookWarning.title' };
    case WebEDIAnnotationCode.OCR_VERIFICATION_WARNING:
      return { severity: 'warning', titleKey: 'webedi:fieldAnnotation.ocrVerificationWarning.title' };
    default:
      annotation satisfies never;
      throw new Error(`Unhandled annotation code: ${(annotation as { code: string }).code}`);
  }
};

/**
 * i18n keys
 * t('webedi:fieldAnnotation.unmatchedArticleWarning.title')
 * t('webedi:fieldAnnotation.ephemeralPartnerWarning.title')
 * t('webedi:fieldAnnotation.connectorHookError.title')
 * t('webedi:fieldAnnotation.connectorHookWarning.title')
 * t('webedi:fieldAnnotation.ocrVerificationWarning.title')
 */
